import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LayoutDashboard, Building2, LogOut, UserCircle2, LifeBuoy, Archive, Trash2, ClipboardList, Stethoscope, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import ErrorBoundary from "@/components/ErrorBoundary";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testId: "admin-nav-dashboard", end: true },
  { to: "/admin/companies", label: "Firmalar", icon: Building2, testId: "admin-nav-companies" },
  { to: "/admin/archive", label: "Arşiv", icon: Archive, testId: "admin-nav-archive" },
  { to: "/admin/deleted", label: "Silinmişler", icon: Trash2, testId: "admin-nav-deleted" },
  { to: "/admin/audit-log", label: "Denetim Kaydı", icon: ClipboardList, testId: "admin-nav-audit-log" },
  { to: "/admin/pricing", label: "Abonelik Fiyatları", icon: Tag, testId: "admin-nav-pricing" },
  { to: "/admin/renewal-diagnostics", label: "Yenileme Teşhisi", icon: Stethoscope, testId: "admin-nav-renewal-diagnostics" },
  { to: "/admin/support", label: "Destek Talepleri", icon: LifeBuoy, testId: "admin-nav-support" },
  { to: "/admin/profile", label: "Profil", icon: UserCircle2, testId: "admin-nav-profile" },
];

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/auth/me");
        setUsername(data?.username || "");
      } catch { /* AdminRoute already handles auth */ }
    })();
  }, []);

  const onLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  return (
    <div
      className="h-screen flex overflow-hidden bg-slate-50"
      data-testid="admin-layout"
    >
      {/* Sabit sidebar — kendi içinde flex-col; uzunluk yeterse yalnızca
          <nav> kendi içinde dikey scroll edebilir.  Ana içerik scroll'u
          sidebar'ı ETKİLEMEZ.  Mobilde (md altı) mevcut davranış aynen
          korunur: sidebar gizlenir, hiçbir yeni drawer eklenmez. */}
      <aside
        className="hidden md:flex md:w-60 h-screen shrink-0 border-r border-slate-200 bg-white flex-col"
        data-testid="admin-sidebar"
      >
        <div className="px-6 pt-7 pb-6 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2">
            <img src="/penz-icon.png" alt="PENZ" className="w-8 h-8 rounded-lg object-contain" />
            <div>
              <div className="font-display font-semibold text-slate-900 tracking-tight">PENZ Admin</div>
              <div className="text-[11px] text-slate-500 uppercase tracking-[0.18em] mt-0.5">Yönetici Paneli</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto px-3 pt-4 pb-2 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testId}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-200 px-3 py-3 space-y-1 shrink-0">
          <div className="px-3 pb-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Yönetici</div>
            <div className="text-sm text-slate-800 font-medium truncate" data-testid="admin-current-username">
              {username || "-"}
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:bg-red-50 hover:text-red-600 h-9 px-3 rounded-lg"
            onClick={onLogout}
            data-testid="admin-logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            <span className="text-sm">Çıkış Yap</span>
          </Button>
        </div>
      </aside>

      {/* Ana içerik — bağımsız scroll konteyneri.  ``.admin-scroll-main``
          class'ı index.css'te her admin sayfasının ``PageHeader``'ını
          otomatik sticky yapar (data-slot="page-header" seçicisi). */}
      <main
        className="flex-1 min-w-0 overflow-y-auto admin-scroll-main"
        data-testid="admin-main"
      >
        <div className="max-w-[1400px] mx-auto w-full px-4 md:px-10 pb-8 md:pb-12">
          <ErrorBoundary>{children}</ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
